import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Spinner,
  Button,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Icon,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  getPlaybackHistory,
  type PlaybackLogEntryDto,
  type PlaybackHistoryPageDto,
} from '../api/playbackHistoryApi';

const PAGE_SIZE = 20;

interface PlaybackHistoryProps {
  locationId: string;
}

export function PlaybackHistory({ locationId }: PlaybackHistoryProps) {
  const { t } = useTranslation();

  const [data, setData] = useState<PlaybackHistoryPageDto | null>(null);
  const [entries, setEntries] = useState<PlaybackLogEntryDto[]>([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchHistory = useCallback(
    async (pageNum: number, reset = false) => {
      setIsLoading(true);
      try {
        const result = await getPlaybackHistory({
          locationId,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          page: pageNum,
          pageSize: PAGE_SIZE,
        });
        setData(result);
        setEntries((prev) => (reset ? result.items : [...prev, ...result.items]));
        setPage(pageNum);
      } catch {
        // Error fetching history
      } finally {
        setIsLoading(false);
      }
    },
    [locationId, startDate, endDate],
  );

  useEffect(() => {
    fetchHistory(1, true);
  }, [fetchHistory]);

  const handleFilter = () => {
    fetchHistory(1, true);
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
  };

  const handleLoadMore = () => {
    if (!data || entries.length >= data.totalCount || isLoading) return;
    fetchHistory(page + 1);
  };

  const formatDuration = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <VStack spacing={4} align="stretch" data-testid="playback-history">
      <Heading size="md">{t('player.history.title')}</Heading>

      {/* Date filter */}
      <HStack spacing={3} flexWrap="wrap">
        <Box>
          <Text fontSize="sm" mb={1}>
            {t('player.history.startDate')}
          </Text>
          <Input
            type="date"
            size="sm"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            data-testid="history-start-date"
          />
        </Box>
        <Box>
          <Text fontSize="sm" mb={1}>
            {t('player.history.endDate')}
          </Text>
          <Input
            type="date"
            size="sm"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            data-testid="history-end-date"
          />
        </Box>
        <HStack spacing={2} alignSelf="flex-end">
          <Button size="sm" colorScheme="blue" onClick={handleFilter} data-testid="history-filter-btn">
            {t('player.history.filter')}
          </Button>
          {(startDate || endDate) && (
            <Button size="sm" variant="ghost" onClick={handleClearFilter} data-testid="history-clear-filter">
              {t('player.history.clearFilter')}
            </Button>
          )}
        </HStack>
      </HStack>

      {/* Results count */}
      {data && (
        <Text fontSize="sm" color="gray.500">
          {data.totalCount} {data.totalCount === 1 ? 'entry' : 'entries'}
        </Text>
      )}

      {/* Loading state */}
      {isLoading && entries.length === 0 && (
        <Box textAlign="center" py={8}>
          <Spinner size="lg" />
        </Box>
      )}

      {/* Empty state */}
      {!isLoading && entries.length === 0 && (
        <Box textAlign="center" py={8} data-testid="history-empty">
          <Text color="gray.500">{t('player.history.noEntries')}</Text>
        </Box>
      )}

      {/* History table */}
      {entries.length > 0 && (
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th></Th>
                <Th>{t('player.history.title')}</Th>
                <Th></Th>
                <Th isNumeric>{t('player.volume')}</Th>
                <Th>{t('player.history.startDate')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {entries.map((entry) => (
                <Tr key={entry.id} data-testid={`history-entry-${entry.id}`}>
                  <Td>
                    {entry.completed ? (
                      <Icon as={CheckCircleIcon} color="green.500" />
                    ) : (
                      <Icon as={ChevronRightIcon} color="gray.400" />
                    )}
                  </Td>
                  <Td>
                    <Text fontWeight="600" fontSize="sm">
                      {entry.track.title || t('player.history.unknownTrack')}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {entry.track.artist || t('player.history.unknownArtist')}
                    </Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={entry.completed ? 'green' : 'yellow'} fontSize="xs">
                      {entry.completed
                        ? t('player.history.completed')
                        : t('player.history.skipped')}
                    </Badge>
                  </Td>
                  <Td isNumeric>{formatDuration(entry.durationPlayed)}</Td>
                  <Td fontSize="xs" color="gray.500">
                    {formatDate(entry.playedAt)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Load more */}
      {data && entries.length < data.totalCount && (
        <Box textAlign="center">
          <Button
            size="sm"
            variant="outline"
            onClick={handleLoadMore}
            isLoading={isLoading}
            data-testid="history-load-more"
          >
            {t('player.history.loadMore')}
          </Button>
        </Box>
      )}
    </VStack>
  );
}
